'use client';

import { UserPickerFields, type UserPickerFieldsProps } from '@slims/ui/molecules';
import { apiFetch } from '@/lib/utils/api';
import { useEffect, useRef, useState } from 'react';

export interface ExistingUser {
	id: string;
	email: string;
	fullName: string | null;
}

/** Current selection reported up to the parent on every change. */
export interface AdminPick {
	email: string;
	fullName: string;
	password: string;
	/** Non-null when the typed email already has an account. */
	existing: ExistingUser | null;
	emailValid: boolean;
	checking: boolean;
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email-first admin entry. The user types an email; we look the account up at
 * `lookupUrl?email=`. If it already exists, only a confirmation is shown (the
 * account is attached). Otherwise the extra details (name + password) appear so
 * a new account can be created. Selection is reported up via `onChange`; the
 * parent owns the submit button and decides what to do with it.
 */
export function AdminUserPicker({
	lookupUrl,
	roleLabel,
	onChange,
	size,
	initialEmail,
}: {
	lookupUrl: string;
	roleLabel: string;
	onChange: (pick: AdminPick) => void;
	size?: UserPickerFieldsProps['size'];
	initialEmail?: string;
}) {
	const [email, setEmail] = useState(initialEmail ?? '');
	const [fullName, setFullName] = useState('');
	const [password, setPassword] = useState('');
	const [existing, setExisting] = useState<ExistingUser | null>(null);
	const [checking, setChecking] = useState(false);

	const emailValid = emailRe.test(email.trim());

	// Debounced account lookup as the email is typed.
	useEffect(() => {
		if (!emailValid) {
			setExisting(null);
			setChecking(false);
			return;
		}
		setChecking(true);
		const handle = setTimeout(async () => {
			try {
				const sep = lookupUrl.includes('?') ? '&' : '?';
				const res = await apiFetch(`${lookupUrl}${sep}email=${encodeURIComponent(email.trim())}`);
				if (res.ok) {
					const data = (await res.json()) as { user: ExistingUser | null };
					setExisting(data.user);
				}
			} finally {
				setChecking(false);
			}
		}, 350);
		return () => clearTimeout(handle);
	}, [email, emailValid, lookupUrl]);

	// Report the current selection up without making onChange a render dependency.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	useEffect(() => {
		onChangeRef.current({ email: email.trim(), fullName, password, existing, emailValid, checking });
	}, [email, fullName, password, existing, emailValid, checking]);

	return (
		<UserPickerFields
			email={email}
			onEmailChange={setEmail}
			fullName={fullName}
			onFullNameChange={setFullName}
			password={password}
			onPasswordChange={setPassword}
			existing={existing}
			emailValid={emailValid}
			checking={checking}
			roleLabel={roleLabel}
			size={size}
		/>
	);
}
